/**
 * Airwallex Cardholder API Types
 *
 * These types define the external API contract with Airwallex for cardholder operations.
 * They are used exclusively for:
 * - Requests to/from Airwallex API (pay-service layer)
 * - Frontend-to-backend wire format (pay-endpoint handlers)
 *
 * Internal domain types (like Cardholder in packages/common/types/cards/) use these
 * types as building blocks but remain separate to isolate API changes from internal
 * business logic.
 *
 * Based on Airwallex API documentation:
 * https://www.airwallex.com/docs/api#/Issuing/Cardholders/
 */

/**
 * Cardholder status in Airwallex system
 */
export enum AWXCardholderStatus {
  PENDING = 'PENDING',
  READY = 'READY',
  DISABLED = 'DISABLED',
  INCOMPLETE = 'INCOMPLETE',
}

/**
 * Cardholder type
 */
export enum AWXCardholderType {
  INDIVIDUAL = 'INDIVIDUAL',
  DELEGATE = 'DELEGATE',
}

/**
 * Name details for individual cardholder
 */
export type AWXName = {
  first_name: string
  last_name: string
  middle_name?: string
  title?: string
}

/**
 * Address information
 */
export type AWXAddress = {
  country: string
  state: string
  city: string
  line1?: string
  line2?: string
  postcode: string
}

/**
 * Identification document details
 */
export type AWXIdentification = {
  country: string
  type: string
  number: string
  expiry_date?: string
  gender?: 'M' | 'F'
  state?: string
  document_front_file_id?: string
  document_back_file_id?: string
}

/**
 * Business identifier for employer
 */
export type AWXBusinessIdentifier = {
  country_code: string
  number: string
  type: string
}

/**
 * Employer information
 */
export type AWXEmployer = {
  business_name: string
  business_identifiers?: AWXBusinessIdentifier[]
}

/**
 * Individual cardholder details
 */
export type AWXIndividual = {
  name: AWXName
  date_of_birth: string // YYYY-MM-DD
  nationality?: string // 2 letter ISO country code
  address: AWXAddress
  identification?: AWXIdentification
  employers?: AWXEmployer[]
  express_consent_obtained: 'yes' | 'no'
  cardholder_agreement_terms_consent_obtained?: 'yes' | 'no' // Canada entities only
  paperless_notification_consent_obtained?: 'yes' | 'no' // Canada entities only
  privacy_policy_terms_consent_obtained?: 'yes' | 'no' // Canada entities only
}

/**
 * Request to create a cardholder in Airwallex
 */
export type CreateAWXCardholderRequest = {
  email: string
  individual: AWXIndividual
  type: AWXCardholderType
  mobile_number: string
  postal_address?: AWXAddress
}

/**
 * Frontend request to create a cardholder (uses userId instead of email)
 * Note: awxConnectedAccountId and organizationId are provided via URL parameters, not request body
 */
export type CreateCardholderRequest = {
  userId: string
  organizationId?: string // Optional since it comes from URL parameter
  individual: AWXIndividual
  type: AWXCardholderType
  mobile_number: string
  postal_address?: AWXAddress
}

/**
 * Response from Airwallex cardholder operations
 */
export type AWXCardholderResponse = {
  cardholder_id: string
  email: string
  individual: AWXIndividual
  type: AWXCardholderType
  status: AWXCardholderStatus
  mobile_number?: string
  postal_address?: AWXAddress
}

/**
 * Request to update a cardholder in Airwallex
 */
export type UpdateAWXCardholderRequest = {
  email?: string
  mobile_number: string
  individual?: Partial<AWXIndividual>
  postal_address?: AWXAddress
  metadata?: { [key: string]: any }
}

/**
 * Response from listing cardholders
 */
export type ListAWXCardholdersResponse = {
  items: AWXCardholderResponse[]
  has_more: boolean
}
